import {
  asTeammateId,
  isTeammateATeamMember,
} from "@repo/app-types/entities/teammate";
import type { FC, ReactNode } from "react";
import { Navigate, useOutlet, useParams } from "react-router";
import { appPaths } from "../app/appPaths.js";
import { requireRouteParam } from "../app/routeParams.js";
import { ParamsRedirect } from "../app/routes/compatRedirects.js";
import { selectTeammate } from "../features/teammate/teammateSlice.js";
import { useAppSelector } from "../hooks/hook.js";
import { NavigationSlideoverContainer } from "../hooks/useModalNavigation.js";

interface TeamMemberTeammateAccessGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Guards access to teammate pages for team member teammates.
 *
 * Team members can:
 * - View their own teammate page and all sub-pages
 * - View any teammate's account-specific routes (accounts/:accountId/...) but
 *   without seeing the background teammate page content
 *
 * If a team member tries to access another teammate's non-account page,
 * they are redirected to their own teammate page.
 */
export const TeamMemberTeammateAccessGuard: FC<
  TeamMemberTeammateAccessGuardProps
> = ({ children, fallback }) => {
  const currentTeammate = useAppSelector(selectTeammate);
  const { teammateId: teammateIdParam, playbookId } = useParams();

  // If no current teammate or no teammateId param, just render children
  if (!currentTeammate || !teammateIdParam || !playbookId) {
    return <>{children}</>;
  }

  const teammateId = asTeammateId(teammateIdParam);

  const isTeamMember = isTeammateATeamMember(currentTeammate);

  // If not a team member or viewing own page, allow full access
  if (!isTeamMember || teammateId === currentTeammate.id) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  // Otherwise, redirect to own teammate page
  return (
    <Navigate
      to={appPaths.teammate.build({
        playbookId,
        teammateId: currentTeammate.id,
      })}
      replace
    />
  );
};

/**
 * Moves a team member from a playbook's customer page, which only the regular
 * route tree serves, to the same customer under their own teammate page.
 * Keeps the path below the customer (an open interaction and its tab) and the
 * query, so report links and their `?quote=` anchors still land.
 */
export const TeamMemberCustomerRedirect: FC = () => {
  const currentTeammate = useAppSelector(selectTeammate);

  // The team member route tree is only picked once the teammate is known.
  if (!currentTeammate) {
    return <Navigate to={appPaths.home.build()} replace />;
  }

  return (
    <ParamsRedirect
      buildTo={(params) =>
        appPaths.teammateCustomer.build({
          playbookId: requireRouteParam(params, "playbookId"),
          teammateId: currentTeammate.id,
          accountId: requireRouteParam(params, "accountId"),
        })
      }
    />
  );
};

/**
 * A navigation slideover container that redirects team members to their own
 * teammate page when the slideover is closed (instead of staying on another
 * teammate's page).
 *
 * If there's no nested route content (user navigated directly to another
 * teammate's page without a specific account), redirects immediately.
 */
export const TeamMemberNavigationSlideoverContainer: FC = () => {
  const currentTeammate = useAppSelector(selectTeammate);
  const { playbookId } = useParams();
  const outlet = useOutlet();

  const ownTeammateUrl =
    currentTeammate && playbookId
      ? appPaths.teammate.build({
          playbookId,
          teammateId: currentTeammate.id,
        })
      : undefined;

  // If there's no nested route content, redirect to own page immediately
  if (!outlet && ownTeammateUrl) {
    return <Navigate to={ownTeammateUrl} replace />;
  }

  return <NavigationSlideoverContainer closeUrl={ownTeammateUrl} />;
};
